import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApiClient } from '../../api/apiClient';
import {
  DocumentTextIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  PauseIcon,
  PlayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

const LiveLogsTab = () => {
  const { t } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000); // 5 seconds
  const [filterLevel, setFilterLevel] = useState('all');
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  const intervalRef = useRef(null);
  const logsEndRef = useRef(null);

  // Scroll to bottom of logs
  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Toggle expanded log message
  const toggleLogExpansion = (index) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedLogs(newExpanded);
  };

  // Format timestamp - convert UTC to local time
  const formatTimestamp = (timestamp) => {
    let date;

    // Handle custom log format: 2025-06-04 12:11:12,384
    if (typeof timestamp === 'string' && timestamp.includes(',')) {
      // Replace comma with dot for milliseconds and parse as UTC
      const normalizedTimestamp = timestamp.replace(',', '.') + 'Z'; // Add Z to indicate UTC
      date = new Date(normalizedTimestamp);
    } else {
      date = new Date(timestamp);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return timestamp || 'Invalid Date';
    }

    // Convert to local time and format (without timezone name to avoid showing UTC)
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Get log level color and icon
  const getLogLevelStyle = (level) => {
    switch (level?.toLowerCase()) {
      case 'error':
        return {
          color: 'text-red-600',
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: XCircleIcon
        };
      case 'warn':
      case 'warning':
        return {
          color: 'text-yellow-600',
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: ExclamationTriangleIcon
        };
      case 'info':
        return {
          color: 'text-blue-600',
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: InformationCircleIcon
        };
      case 'debug':
        return {
          color: 'text-purple-600',
          bg: 'bg-purple-50',
          border: 'border-purple-200',
          icon: DocumentTextIcon
        };
      case 'success':
        return {
          color: 'text-green-600',
          bg: 'bg-green-50',
          border: 'border-green-200',
          icon: CheckCircleIcon
        };
      default:
        return {
          color: 'text-gray-600',
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          icon: DocumentTextIcon
        };
    }
  };

  // Parse plain text log entry
  const parseLogEntry = (logText) => {
    // Regex to match: YYYY-MM-DD HH:mm:ss,SSS LEVEL [logger] (thread) message
    const logRegex = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3})\s+(\w+)\s+\[([^\]]+)\]\s+\(([^)]+)\)\s+(.*)$/;

    // Split by lines to handle multi-line logs (like stack traces)
    const lines = logText.split('\n');
    const firstLine = lines[0];
    const match = firstLine.match(logRegex);

    if (match) {
      const firstLineMessage = match[5];
      // For multi-line logs, fullMessage includes the complete log (first line + stack trace)
      const fullMessage = lines.length > 1 ? logText : firstLineMessage;

      return {
        timestamp: match[1],
        level: match[2],
        logger: match[3],
        thread: match[4],
        message: firstLineMessage, // Only the first line message
        fullMessage: fullMessage, // Complete log including first line and stack trace
        originalText: logText
      };
    }

    // Fallback for logs that don't match the expected format
    return {
      timestamp: new Date().toISOString(),
      level: 'INFO',
      logger: 'unknown',
      thread: 'unknown',
      message: logText,
      fullMessage: logText, // Store full message
      originalText: logText
    };
  };

  // Fetch logs from API
  const fetchLogs = useCallback(async () => {
    try {
      const client = getApiClient();
      const response = await client.get('/admin/logs');

      // Handle different response formats
      let rawLogs = [];

      if (response.data) {
        if (Array.isArray(response.data)) {
          rawLogs = response.data;
        } else if (response.data.logs && Array.isArray(response.data.logs)) {
          rawLogs = response.data.logs;
        } else if (typeof response.data === 'string') {
          // Handle single string response - split by log entries (not just newlines)
          // Each log entry starts with a timestamp pattern
          const logPattern = /(?=\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3})/;
          rawLogs = response.data.split(logPattern).filter(line => line.trim() !== '');
          console.log('Split logs into', rawLogs.length, 'entries');
        } else if (typeof response.data === 'object') {
          // Handle object response - try to extract logs or convert to array
          rawLogs = [response.data];
        }
      }

      const parsedLogs = rawLogs.map(log => {
        if (typeof log === 'string') {
          const parsed = parseLogEntry(log);
          console.log('Parsed log:', {
            hasFullMessage: !!parsed.fullMessage,
            messageLength: parsed.message?.length,
            fullMessageLength: parsed.fullMessage?.length,
            isDifferent: parsed.fullMessage !== parsed.message
          });
          return parsed;
        }
        // If it's already an object, use it as is but ensure it has required fields
        return {
          timestamp: log.timestamp || log.createdAt || new Date().toISOString(),
          level: log.level || 'INFO',
          logger: log.logger || log.source || 'unknown',
          thread: log.thread || 'unknown',
          message: log.message || log.text || JSON.stringify(log),
          fullMessage: log.message || log.text || JSON.stringify(log), // Store full message
          originalText: log.originalText || JSON.stringify(log),
          ...log
        };
      });

      setLogs(parsedLogs);
      setError(null);
    } catch (error) {
      console.error('Error fetching logs:', error);
      setError(error.message || 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter logs by level
  const filteredLogs = logs.filter(log => {
    // Exclude Firebase token validator logs
    if (log.logger && log.logger.includes('org.ges.fir.FirebaseTokenValidator')) {
      return false;
    }

    // Filter by log level
    if (filterLevel === 'all') return true;
    return log.level?.toLowerCase() === filterLevel.toLowerCase();
  });

  // Setup auto-refresh
  useEffect(() => {
    if (isAutoRefresh) {
      fetchLogs();
      intervalRef.current = setInterval(fetchLogs, refreshInterval);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoRefresh, refreshInterval, fetchLogs]);

  // Initial fetch
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Remove auto-scroll functionality - commented out to allow manual scrolling
  // useEffect(() => {
  //   if (logs.length > 0) {
  //     scrollToBottom();
  //   }
  // }, [logs]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <ArrowPathIcon className="animate-spin h-12 w-12 text-purple-600 mx-auto" />
          <p className="mt-4 text-lg text-gray-600">{t('loadingLogs')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-6 sm:px-6 md:px-8">
      {/* Controls */}
      <div className="bg-white shadow-lg rounded-t-xl p-6 border border-slate-200 border-b-0">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('liveLogs')}</h3>
            <span className="flex items-center text-sm text-gray-500">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
              {filteredLogs.length} {t('entries')}
            </span>
          </div>

          <div className="flex items-center space-x-4">
            {/* Filter */}
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All</option>
              <option value="info">Info</option>
              <option value="warn">Warn</option>
              <option value="debug">Debug</option>
              <option value="error">Error</option>
            </select>

            {/* Refresh Interval */}
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              disabled={!isAutoRefresh}
            >
              <option value={1000}>1s</option>
              <option value={5000}>5s</option>
              <option value={10000}>10s</option>
              <option value={30000}>30s</option>
            </select>

            {/* Auto-refresh toggle */}
            <button
              onClick={() => setIsAutoRefresh(!isAutoRefresh)}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${isAutoRefresh
                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
            >
              {isAutoRefresh ? (
                <>
                  <PauseIcon className="w-4 h-4 mr-2" />
                  {t('pause')}
                </>
              ) : (
                <>
                  <PlayIcon className="w-4 h-4 mr-2" />
                  {t('resume')}
                </>
              )}
            </button>

            {/* Manual refresh */}
            <button
              onClick={fetchLogs}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 transition-colors"
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              {t('refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 p-4 border-t-0 border-b-0">
          <div className="flex items-center">
            <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-800">{t('error')}: {error}</span>
          </div>
        </div>
      )}

      {/* Logs Display */}
      <div className="bg-white shadow-lg rounded-b-xl border border-slate-200 border-t-0 overflow-hidden relative">
        <div className="h-96 overflow-y-auto">
          {filteredLogs.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {filteredLogs.map((log, index) => {
                const style = getLogLevelStyle(log.level);
                const IconComponent = style.icon;

                return (
                  <div key={index} className={`p-4 hover:bg-gray-50 transition-colors ${style.bg}`}>
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <IconComponent className={`w-5 h-5 ${style.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${style.bg} ${style.color} border ${style.border}`}>
                              {log.level?.toUpperCase() || 'LOG'}
                            </span>
                            {log.logger && log.logger !== 'unknown' && (
                              <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                {log.logger}
                              </span>
                            )}
                            {log.thread && log.thread !== 'unknown' && (
                              <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                {log.thread}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 flex items-center">
                            <ClockIcon className="w-3 h-3 mr-1" />
                            {formatTimestamp(log.timestamp || log.createdAt || new Date())}
                          </span>
                        </div>
                        <div className="mt-2">
                          <div className="flex items-start justify-between">
                            <pre className="text-sm text-gray-900 break-words whitespace-pre-wrap font-mono flex-1 mr-2">
                              {expandedLogs.has(index) ?
                                (log.fullMessage || log.message) :
                                (log.message && log.message.length > 200 ?
                                  log.message.substring(0, 200) + '...' :
                                  log.message)
                              }
                            </pre>
                            {log.message && log.message.length > 200 && (
                              <button
                                onClick={() => toggleLogExpansion(index)}
                                className="flex-shrink-0 flex items-center text-xs text-purple-600 hover:text-purple-800 bg-purple-50 hover:bg-purple-100 px-3 py-2 rounded-md transition-colors border border-purple-200 font-medium"
                                title={`Message length: ${log.message.length} characters`}
                              >
                                {expandedLogs.has(index) ? (
                                  <>
                                    <ChevronUpIcon className="w-3 h-3 mr-1" />
                                    Collapse
                                  </>
                                ) : (
                                  <>
                                    <EyeIcon className="w-3 h-3 mr-1" />
                                    View Full
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                        {(log.fullMessage && log.fullMessage !== log.message && log.fullMessage.trim() !== '') && (
                          <details className="mt-2">
                            <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-800">
                              {t('rawLog')}
                            </summary>
                            <pre className="mt-1 text-xs text-gray-600 bg-gray-100 p-2 rounded overflow-x-auto font-mono whitespace-pre-wrap break-words">
                              {log.fullMessage}
                            </pre>
                          </details>
                        )}
                        {log.meta && Object.keys(log.meta).length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-800">
                              {t('metadata')}
                            </summary>
                            <pre className="mt-1 text-xs text-gray-600 bg-gray-100 p-2 rounded overflow-x-auto">
                              {JSON.stringify(log.meta, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={logsEndRef} />
            </div>
          ) : (
            <div className="text-center py-12">
              <DocumentTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {filterLevel === 'all' ? t('noLogsAvailable') : t('noLogsForFilter')}
              </p>
            </div>
          )}
        </div>

        {/* Go to Bottom Button */}
        {filteredLogs.length > 0 && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-4 right-4 bg-purple-600 hover:bg-purple-700 text-white p-2 rounded-full shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            title={t('goToBottom') || 'Go to bottom'}
          >
            <ChevronDownIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default LiveLogsTab;
